import { useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { artistNamesKey, normalizeArtistNames } from "@produce/shared";
import { prefetchArtistImages } from "@/lib/artistImagePrefetch";
import { useSpotifyImagesEnabled } from "./useArtistImage";

/**
 * Warm the artist photos for a whole lineup when a schedule page opens, so the
 * artist popup shows its photo instantly instead of fetching on first click.
 *
 * By default this resolves photo URLs only — that removes the edge-function
 * round-trip, which is the wait producers actually notice, at negligible cost.
 * Pass `download` for the subset whose image bytes should come down too (the
 * day currently on screen, say); downloading a 250-artist lineup is tens of
 * megabytes and only pays off for photos someone is about to open.
 *
 * No-ops when the admin toggle is off or the list is empty, and stops when the
 * screen that started it goes away.
 */
export function useArtistImagePrefetch(
  artistNames: (string | null | undefined)[] | undefined,
  download?: (string | null | undefined)[],
): void {
  const queryClient = useQueryClient();
  const featureEnabled = useSpotifyImagesEnabled();

  // Memoised: the schedule creator re-renders on every mousemove while a block
  // is being dragged, and normalising a few hundred names on each of those
  // frames is real work for a result that virtually never changes. Empty while
  // the feature is off, so nothing is normalised for nothing.
  const names = useMemo(
    () => (featureEnabled ? normalizeArtistNames(artistNames ?? []) : []),
    [featureEnabled, artistNames],
  );
  const downloadNames = useMemo(
    () => (featureEnabled ? normalizeArtistNames(download ?? []) : []),
    [featureEnabled, download],
  );

  // Stable dependencies: re-run only when the actual sets of names change.
  const namesKey = artistNamesKey(names);
  const downloadKey = artistNamesKey(downloadNames);

  useEffect(() => {
    if (names.length === 0) return;

    let cancelled = false;
    void prefetchArtistImages(queryClient, names, {
      download: downloadNames,
      isCancelled: () => cancelled,
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namesKey, downloadKey]);
}
